import { useState } from 'react'

import { Volume } from '@/api/type-server'
import { NumberInput, Trans } from '@/components'
import { formatRegion } from '@/utils/formatter'
import { cn } from '@/utils/style'

import { SelectedData } from './volume-quota-modal'

interface VolumeQuotaSection {
  isSnapshot: boolean
  volumes: Volume[]
  availableInGB: number
  summarizedData: SelectedData
  onChange: (volume: Volume, value: number) => void
  warningVolumeIds?: Set<number>
}

export default function VolumeQuotaSection({
  isSnapshot,
  volumes = [],
  availableInGB,
  summarizedData,
  onChange,
  warningVolumeIds,
}: VolumeQuotaSection) {
  const [showWarningFor, setShowWarningFor] = useState<number | null>(null)

  if (volumes.length === 0) {
    return null
  }
  return (
    <div className="">
      <h3 className="text-primary mb-4">
        {isSnapshot ? (
          <Trans i18nKey="space.volumeItemTitleSnapshot">Snapshot</Trans>
        ) : (
          <Trans i18nKey="space.volumeItemTitleCloudStorage">Data Hub</Trans>
        )}
      </h3>
      <div className="gap-4">
        {volumes.map((volume) => {
          const usage = Number(((volume?.usage_mb || 0) / 1024).toFixed(2))
          const currentQuota = volume.quota_gb || 0
          let selectedQuotaInGB = currentQuota
          const index = summarizedData.selectedData.findIndex((val) => val.volume.id === volume.id)
          if (index !== -1) {
            selectedQuotaInGB = summarizedData.selectedData[index].selectedQuotaInGB
          }
          const quota = selectedQuotaInGB + availableInGB || 0

          const insufficientSpace =
            summarizedData.lastestVolume &&
            volume.id === summarizedData.lastestVolume?.id &&
            availableInGB < 0

          // Check if this specific volume has insufficient quota (quota < used space but > 0)
          const hasInsufficientQuota = selectedQuotaInGB > 0 && selectedQuotaInGB < usage

          return (
            <div key={`quota.${volume.id}`}>
              <div
                key={`volume${isSnapshot}${volume.id}`}
                className="flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className="w-[120px]">
                    <div className="flex items-baseline t-sm text-secondary">
                      <div
                        className={cn(
                          ' w-3 h-3 mr-2',
                          isSnapshot ? ' bg-btn-tertiary' : 'bg-btn-secondary',
                        )}
                      />
                      {isSnapshot ? (
                        <Trans i18nKey="space.volumeItemSubTitleStorage">Storage</Trans>
                      ) : (
                        formatRegion(volume.domain)
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <NumberInput
                      value={selectedQuotaInGB}
                      onChange={(value) => {
                        if (value > 0 && showWarningFor === volume.id) {
                          setShowWarningFor(null)
                        }
                        onChange(volume, value)
                      }}
                      min={0}
                      max={quota}
                      step={1}
                      className="w-[160px]"
                      disabled={quota < 1}
                    />
                    <div className=" t-sm text-secondary">GB</div>
                  </div>
                </div>
              </div>
              {insufficientSpace && (
                <div className="ml-[120px] mt-2 pl-3 t-sm text-error">
                  <Trans i18nKey="space.volumeItemSubTitleInsufficient">
                    Insufficient available space
                  </Trans>
                </div>
              )}
              {hasInsufficientQuota && (
                <div className="ml-[120px] mt-2 pl-3 t-sm text-error">
                  <Trans
                    i18nKey="space.volumeClearWarning"
                    values={{ newQuota: selectedQuotaInGB, usedSpace: usage }}
                  />
                </div>
              )}
              {showWarningFor === volume.id && (
                <div className="ml-[120px] mt-2 pl-3 t-sm text-error">
                  <Trans i18nKey="space.volumeClearWarning">
                    This action will permanently delete all data stored in Snapshots.
                  </Trans>
                </div>
              )}
              {warningVolumeIds?.has(volume.id) && (
                <div className="ml-[120px] mt-2 pl-3 t-sm text-error">
                  <Trans
                    i18nKey={
                      isSnapshot ? 'space.snapshotClearWarning' : 'space.datadriveClearWarning'
                    }
                  >
                    {isSnapshot
                      ? 'To reduce quota to 0, please manually delete all data usage first.'
                      : 'To reduce quota to 0, please manually delete all data usage first.'}
                  </Trans>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
